import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { darken } from 'polished';
import theme, { textStyles as ts } from '../../theme';

const DestructiveAction = styled.button`
  ${ts.body};
  color: ${theme('colors.destructive')};
  background-color: transparent;
  cursor: pointer;
  display: inline-block;
  padding-top: ${theme('spacing.small')};
`;
const MultiLineTextWrapper = styled.span`
  flex: 1;
  padding: ${theme('spacing.regular')} 0;
`;

// Input
const HiddenInput = styled.input`
  opacity: 0;
  position: absolute;

  &:checked ~ span > svg * {
    stroke: white;
  }
  &:checked ~ span > svg .fill {
    fill: white;
    stroke: none;
  }
  &:checked ~ span > svg .fillStroke {
    fill: white;
    stroke: white;
  }
  &:checked ~ span > svg .noStroke {
    stroke: none;
  }
`;

// Text accessory
export const TextAccessory = styled.p`
  ${ts.title};
  float: right;
`;

// Outer container
const wrapperStyle = css`
  width: 100%;
  display: flex;
  position: relative;

  & * {
    z-index: 1;
  }
`;
const PlainWrapper = styled.div`
  ${wrapperStyle};
`;
const LabelWrapper = styled.label`
  ${wrapperStyle} 
  cursor: pointer;

  &:hover svg * {
    stroke: white;
  }
  &:hover .fill {
    fill: white;
    stroke: none;
  }
  &:hover .fillStroke {
    fill: white;
    stroke: white;
  }
  &:hover .noStroke {
    stroke: none;
  }
`;

const IconWrapper = styled.span`
  padding: ${({ small, theme: t }) => (small ? `${t.spacing.small} ${t.spacing.small}` : `${t.spacing.regular} ${t.spacing.large} 0 0 `)};
`;

// Text elements
const Title = styled.h5`
  ${ts.boldTitle};
  ${LabelWrapper}:hover & {
    color: white;
  }
  ${HiddenInput}:checked ~ span & {
    color: white;
  }
  padding-bottom: 6px !important;
`;

const Subtitle = styled.p`
  ${ts.body};
  ${HiddenInput}:checked ~ span & {
    color: ${theme('colors.textWhite').apply(col => darken(0.3, col))};
  }
  ${LabelWrapper}:hover & {
    color: ${theme('colors.textWhite').apply(col => darken(0.3, col))};
  }
`;

// Highlight background
const Highlight = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0px;
  bottom: 0px;
  background-color: ${theme('colors.backgroundVeryDark')};
  z-index: 0;
  border-radius: ${theme('radius.regular')};
  cursor: pointer;

  ${HiddenInput}:checked ~ & {
    background-color: ${theme('colors.accentSecondary')};
  }
  ${LabelWrapper}:hover & {
    background-color: ${theme('colors.accentSecondary')};
  }
`;

const IconPad = styled.span`
  padding: 0 0 0 ${theme('spacing.regular')};
  display: block;
`;

const Accessory = styled.div`
  * {
    font-weight: 600 !important;
  }
  padding: ${theme('spacing.regular')} ${theme('spacing.regular')} 0 0;
`;

const RowComponent = ({
  title,
  titleWrapper,
  subtitle,
  destructiveActionText,
  icon,
  accessory,
  selected,
  inputType,
  inputName,
  inputValue,
  onDestructiveAction,
  onInputChange,
  destructiveFormAction,
  small,
  isND,
  isMCS,
  medicineStatus,
}) => {
  const RowWrapper = inputType ? LabelWrapper : PlainWrapper;
  const TitleWrapper = (subtitle === null && !titleWrapper) ? PlainWrapper : Title;
  const Destruct = destructiveActionText ? (
    <DestructiveAction
      onClick={onDestructiveAction}
      formAction={destructiveFormAction}
    >
      {destructiveActionText}
    </DestructiveAction>
  ) : null;
  return (
    <RowWrapper>
      {inputType ? (
        <HiddenInput
          type={inputType}
          name={inputName}
          selected={selected}
          onChange={onInputChange}
          value={inputValue}
          checked={selected && ['radio', 'checkbox'].includes(inputType) ? 'selected' : undefined}
        />
      ) : null}
      {(subtitle !== null || titleWrapper) ? <IconPad /> : null }
      {icon != null ? <IconWrapper small={small}>{icon}</IconWrapper> : null}
      <MultiLineTextWrapper small={small}>
        <TitleWrapper>
          {isND ? 'Not dispensed: ' :
            (isMCS && medicineStatus === 'dispatched') ? 'Dispatched: ' :
              (isMCS ? 'Waiting for stock: ' : '')}
          {title}
        </TitleWrapper>
        {subtitle !== null && !small && (
          <>
            <Subtitle>
              {subtitle}
            </Subtitle>
            {isND && (
              <Subtitle>
                <br />
                We are unable to dispense this item due to supply problems. We will mark this item as not dispensed on the NHS system so your GP can see that you haven’t received this medication from us.
              </Subtitle>
            )}
          </>
        )}
        {Destruct}
      </MultiLineTextWrapper>
      {accessory != null ? (
        <Accessory selected={selected} selectable={inputType !== null}>
          {accessory}
        </Accessory>
      ) : null}
      {inputType || selected ? <Highlight selected={selected} /> : null}
    </RowWrapper>
  );
};

RowComponent.propTypes = {
  title: PropTypes.string.isRequired,
  titleWrapper: PropTypes.bool,
  subtitle: PropTypes.string,
  destructiveActionText: PropTypes.string,
  icon: PropTypes.node,
  accessory: PropTypes.element,
  selected: PropTypes.bool,
  inputType: PropTypes.oneOf(['radio', 'checkbox', null]),
  inputName: PropTypes.string,
  inputValue: PropTypes.string,
  onInputChange: PropTypes.func,
  onDestructiveAction: PropTypes.func,
  destructiveFormAction: PropTypes.string,
  small: PropTypes.bool,
  isND: PropTypes.bool,
  isMCS: PropTypes.bool,
  medicineStatus: PropTypes.string,
};

RowComponent.defaultProps = {
  titleWrapper: false,
  subtitle: null,
  selected: false,
  icon: null,
  accessory: null,
  inputType: null,
  inputName: '',
  inputValue: null,
  destructiveActionText: null,
  onInputChange: () => {},
  onDestructiveAction: () => {},
  destructiveFormAction: null,
  small: false,
  isND: false,
  isMCS: false,
  medicineStatus: '',
};

export default RowComponent;
